import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type FormEvent,
  type ChangeEvent,
} from 'react';
import PortalPagination from 'volto-ministerio-publico-federal/components/Pagination';
import type { SearchViewProps } from './types';
import {
  PORTAL_TYPES,
  getDateFilterOptions,
  SORT_OPTIONS,
  DEFAULT_PORTAL_TYPES,
} from './config';
import { RssIcon } from '@phosphor-icons/react';
import {
  MPFButton,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';

export const SearchView = ({
  totalResults = 0,
  results = [],
  rssUrl = '',
  onSearchSubmit,
  currentPage = 1,
  defaultPageSize = 25,
  batching = undefined,
  onPaginationChange,
  onSortChange,
  loading = false,
  initialState = {},
  portalTypes,
  dateOptions,
}: SearchViewProps) => {
  const effectivePortalTypes = useMemo(
    () => portalTypes || PORTAL_TYPES,
    [portalTypes],
  );

  const effectiveDateOptions = useMemo(
    () => dateOptions || getDateFilterOptions(),
    [dateOptions],
  );

  const [searchText, setSearchText] = useState<string>(
    initialState.searchText ?? '',
  );
  const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(
    initialState.isFilterExpanded ?? false,
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    initialState.selectedTypes ?? DEFAULT_PORTAL_TYPES,
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    initialState.selectedDate ?? '1970-01-02',
  );
  const [sortBy, setSortBy] = useState<string>(
    initialState.sortBy ?? 'relevance',
  );
  const [sortOrder, setSortOrder] = useState<string>(
    initialState.sortOrder ?? '',
  );

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onSearchSubmit) {
      onSearchSubmit({
        searchText,
        isFilterExpanded,
        selectedTypes,
        selectedDate,
        sortBy,
        sortOrder,
      });
    }
    // isFilterExpanded, sortBy, sortOrder são intencionalmente omitidos
    // pois queremos disparar apenas quando filtros de conteúdo mudam
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, selectedDate, onSearchSubmit]);

  const handleToggleFilter = useCallback(() => {
    setIsFilterExpanded((prev) => !prev);
  }, []);

  const handleToggleAllTypes = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelectedTypes(effectivePortalTypes.map((type) => type.value));
      } else {
        setSelectedTypes([]);
      }
    },
    [effectivePortalTypes],
  );

  const handleTypeChange = useCallback((value: string) => {
    setSelectedTypes((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  }, []);

  const handleSortChange = useCallback(
    (value: string, order: string = '') => {
      setSortBy(value);
      setSortOrder(order);
      onSortChange?.(value, order);
    },
    [onSortChange],
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onSearchSubmit) {
      onSearchSubmit({
        searchText,
        isFilterExpanded,
        selectedTypes,
        selectedDate,
        sortBy,
        sortOrder,
      });
    }
  };

  const isAllTypesSelected =
    selectedTypes.length === effectivePortalTypes.length;
  const totalPages = Math.ceil(totalResults / defaultPageSize);

  return (
    <section
      className="mpf-search"
      id="search-results"
      role="search"
      aria-live="polite"
      tabIndex={-1}
      suppressHydrationWarning
    >
      <form
        name="searchform"
        id="searchform"
        role="search"
        className="mpf-search__form"
        onSubmit={handleSubmit}
      >
        <div className="mpf-search__input-group">
          <input
            id="SearchableText"
            name="SearchableText"
            type="text"
            title="Busca no Site"
            placeholder="Digite o termo para busca..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="mpf-search__input"
          />
          <MPFButton
            type="reset"
            variant="ghost"
            onClick={() => setSearchText('')}
            className="mpf-search__reset-button"
          >
            Limpar
          </MPFButton>
          <MPFButton type="submit" variant="primary">
            Buscar
          </MPFButton>
        </div>

        <div className="mpf-search__filter-wrapper">
          <MPFButton
            variant="unstyled"
            id="search-filter-toggle"
            aria-expanded={isFilterExpanded}
            aria-controls="search-filter-content"
            onClick={handleToggleFilter}
            className="mpf-search__filter-toggle"
          >
            Filtrar os resultados
          </MPFButton>

          {isFilterExpanded && (
            <div
              id="search-filter-content"
              className="mpf-search__filter-content"
            >
              <fieldset className="mpf-search__fieldset">
                <legend className="mpf-search__legend">Tipo de item</legend>
                <div className="mpf-search__toggle-all">
                  <input
                    type="checkbox"
                    id="pt_toggle"
                    name="pt_toggle"
                    checked={isAllTypesSelected}
                    onChange={handleToggleAllTypes}
                    className="mpf-search__checkbox"
                  />
                  <label htmlFor="pt_toggle">Selecionar Todos/Nenhum</label>
                </div>

                <div className="mpf-search__type-options">
                  {effectivePortalTypes.map((type) => (
                    <div key={type.id} className="mpf-search__type-option">
                      <input
                        type="checkbox"
                        id={type.id}
                        name="portal_type"
                        value={type.value}
                        checked={selectedTypes.includes(type.value)}
                        onChange={() => handleTypeChange(type.value)}
                        className="mpf-search__checkbox"
                      />
                      <label htmlFor={type.id}>{type.label}</label>
                    </div>
                  ))}
                </div>
              </fieldset>

              <fieldset className="mpf-search__fieldset">
                <legend className="mpf-search__legend">Resultados desde</legend>
                <div className="mpf-search__date-options">
                  {effectiveDateOptions.map((dateOpt) => (
                    <div key={dateOpt.id} className="mpf-search__date-option">
                      <input
                        type="radio"
                        id={dateOpt.id}
                        name="created_date"
                        value={dateOpt.value}
                        checked={selectedDate === dateOpt.value}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="mpf-search__radio"
                      />
                      <label htmlFor={dateOpt.id}>{dateOpt.label}</label>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
          )}
        </div>

        <header className="mpf-search__header">
          <h1 className="mpf-search__heading">Resultado da busca</h1>

          <p className="mpf-search__rss-subscription">
            <RssIcon className="mpf-search__rss-icon" color="orange" />
            <a href={rssUrl} className="mpf-search__rss-link">
              <span>Assinar um RSS sempre atualizado.</span>
            </a>
          </p>
        </header>

        <div className="mpf-search__results-wrapper">
          <div className="mpf-search__results-bar">
            <span className="mpf-search__results-count">
              <strong className="mpf-search__results-number">
                {totalResults}
              </strong>{' '}
              ite{totalResults === 1 ? 'm' : 'ns'} atend
              {totalResults === 1 ? 'u' : 'em'} ao seu critério.
            </span>
          </div>

          <nav
            className="mpf-search__sort-nav"
            aria-label="Opções de ordenação"
          >
            <span className="mpf-search__sort-label">Ordenar por: </span>
            <div className="mpf-search__sort-options">
              {SORT_OPTIONS.map((option) => {
                const isActive = sortBy === option.value;
                return (
                  <MPFButton
                    key={option.id}
                    variant="unstyled"
                    onClick={() =>
                      handleSortChange(option.value, option.order || '')
                    }
                    className={`mpf-search__sort-button mpf-btn mpf-btn--small mpf-btn-brand ${
                      isActive ? 'mpf-search__sort-button--active' : ''
                    }`}
                  >
                    {option.label}
                  </MPFButton>
                );
              })}
            </div>
          </nav>

          <div className="mpf-search__results">
            <div
              className="mpf-search__no-results"
              style={{ display: results.length === 0 ? 'block' : 'none' }}
            >
              Nenhum resultado encontrado
            </div>

            <ol
              className="mpf-search__results-list"
              style={{ display: results.length > 0 ? 'block' : 'none' }}
            >
              {results.map((item) => (
                <li key={item.id} className="mpf-search__result-item">
                  <h2 className="mpf-search__result-title">
                    <MPFLink
                      href={item.url}
                      className={`mpf-search__result-link ${
                        item.reviewState
                          ? `mpf-search__result-link--${item.reviewState}`
                          : ''
                      }`}
                    >
                      {item.title}
                    </MPFLink>
                  </h2>

                  {item.description && (
                    <div className="mpf-search__result-description">
                      <span>{item.description}</span>
                    </div>
                  )}

                  {item.location && item.location.length > 0 && (
                    <cite className="mpf-search__result-location">
                      <span className="mpf-search__location-label">
                        Localizado em
                      </span>
                      {item.location.map((loc, index) => (
                        <span
                          key={loc.url + index}
                          className="mpf-search__location-path"
                        >
                          <MPFLink
                            href={loc.url}
                            className="mpf-search__location-link"
                          >
                            {loc.title}
                          </MPFLink>
                          {index < item.location.length - 1 && (
                            <span className="mpf-search__location-separator">
                              {' '}
                              /{' '}
                            </span>
                          )}
                        </span>
                      ))}
                    </cite>
                  )}
                </li>
              ))}
            </ol>
          </div>

          {batching && totalPages > 1 && (
            <div className="mpf-search__pagination">
              <PortalPagination
                batching={batching}
                currentPage={currentPage}
                totalItems={totalResults}
                defaultPageSize={defaultPageSize}
                onPaginationChange={(
                  _e: React.SyntheticEvent | null,
                  { activePage }: { activePage: number },
                ) => onPaginationChange?.(activePage)}
              />
            </div>
          )}
        </div>
      </form>
    </section>
  );
};
